import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import io from 'socket.io-client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
    Users,
    MessageSquare,
    Radio,
    Trash2,
    Search,
    LogOut,
    Shield,
    AlertTriangle,
    TrendingUp,
    Activity,
    Eye,
    Flag,
    CheckCircle,
    XCircle,
    Ban,
    RotateCcw,
    HelpCircle
} from 'lucide-react';
import { toast } from 'sonner';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = ({ user, onSignOut }) => {
    const navigate = useNavigate();
    const [stats, setStats] = useState({
        totalUsers: 0,
        totalPopups: 0,
        totalMessages: 0,
        activePopups: 0
    });
    const [popups, setPopups] = useState([]);
    const [users, setUsers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('overview');
    const [deleteDialog, setDeleteDialog] = useState({ open: false, popup: null });
    const [reports, setReports] = useState([]);
    const [reportFilter, setReportFilter] = useState('all');
    const [actionDialog, setActionDialog] = useState({ open: false, report: null, action: null });
    const [feedback, setFeedback] = useState([]);

    // Check if user is super_admin
    useEffect(() => {
        if (!user || user.role !== 'super_admin') {
            toast.error('Access denied. Admin only.');
            navigate('/dashboard');
        }
    }, [user, navigate]);

    // Fetch admin stats
    useEffect(() => {
        fetchStats();
        fetchPopups();
        fetchUsers();
        fetchReports();
        fetchFeedback();
    }, []);

    // Realtime socket connection for admin dashboard
    useEffect(() => {
        const socket = io(BACKEND_URL, {
            path: '/api/socket.io',
            transports: ['websocket', 'polling']
        });

        socket.on('connect', () => {
            console.log('Admin socket connected');
        });

        // Listen for new feedback
        socket.on('new_feedback', () => {
            fetchFeedback();
        });

        // Listen for new reports
        socket.on('new_report', () => {
            fetchReports();
        });

        // Listen for popup changes
        socket.on('popup_created', () => {
            fetchPopups();
            fetchStats();
        });

        socket.on('popup_removed', () => {
            fetchPopups();
            fetchStats();
        });

        // Listen for user changes
        socket.on('user_registered', () => {
            fetchUsers();
            fetchStats();
        });

        // Refresh stats periodically (every 30 seconds as fallback)
        const interval = setInterval(() => {
            fetchStats();
        }, 30000);

        return () => {
            socket.disconnect();
            clearInterval(interval);
        };
    }, []);

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem('chatAppToken');
            const response = await axios.get(`${API}/admin/stats`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setStats(response.data);
        } catch (error) {
            console.error('Error fetching stats:', error);
        }
    };

    const fetchPopups = async () => {
        try {
            const token = localStorage.getItem('chatAppToken');
            const response = await axios.get(`${API}/admin/popups`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setPopups(response.data);
        } catch (error) {
            console.error('Error fetching popups:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchUsers = async () => {
        try {
            const token = localStorage.getItem('chatAppToken');
            const response = await axios.get(`${API}/admin/users`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUsers(response.data);
        } catch (error) {
            console.error('Error fetching users:', error);
        }
    };

    const fetchReports = async () => {
        try {
            const token = localStorage.getItem('chatAppToken');
            const response = await axios.get(`${API}/admin/reports`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setReports(response.data);
        } catch (error) {
            console.error('Error fetching reports:', error);
        }
    };

    const fetchFeedback = async () => {
        try {
            const token = localStorage.getItem('chatAppToken');
            const response = await axios.get(`${API}/admin/feedback`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setFeedback(response.data);
        } catch (error) {
            console.error('Error fetching feedback:', error);
        }
    };

    const handleFeedbackStatus = async (feedbackId, status) => {
        try {
            const token = localStorage.getItem('chatAppToken');
            await axios.put(`${API}/admin/feedback/${feedbackId}/status?status=${status}`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success(`Feedback marked as ${status}`);
            fetchFeedback();
        } catch (error) {
            console.error('Error updating feedback:', error);
            toast.error('Failed to update feedback');
        }
    };

    const handleReportAction = async (action) => {
        if (!actionDialog.report) return;

        try {
            const token = localStorage.getItem('chatAppToken');
            await axios.patch(`${API}/admin/reports/${actionDialog.report.report_id}`, {
                action: action,
                notes: `Action taken by ${user?.username}`
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            toast.success(`Action '${action}' taken successfully`);
            setActionDialog({ open: false, report: null, action: null });
            fetchReports();
            fetchStats();
        } catch (error) {
            console.error('Error taking action:', error);
            toast.error('Failed to take action');
        }
    };

    const handleDeletePopup = async () => {
        if (!deleteDialog.popup) return;

        try {
            const token = localStorage.getItem('chatAppToken');
            await axios.delete(`${API}/admin/popups/${deleteDialog.popup.id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            toast.success(`Popup "${deleteDialog.popup.name}" deleted successfully`);
            setDeleteDialog({ open: false, popup: null });
            fetchPopups();
            fetchStats();
        } catch (error) {
            console.error('Error deleting popup:', error);
            toast.error('Failed to delete popup');
        }
    };

    const handleSignOut = () => {
        localStorage.removeItem('chatAppToken');
        localStorage.removeItem('chatAppUser');
        onSignOut();
        navigate('/signin');
    };

    const filteredPopups = popups.filter(popup =>
        popup.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        popup.admin_username.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const filteredUsers = users.filter(u =>
        u.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
        u.email.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const filteredReports = reports.filter(r => {
        if (reportFilter === 'all') return true;
        return r.status === reportFilter;
    });

    const getPriorityColor = (reportCount) => {
        if (reportCount >= 5) return { bg: 'bg-red-600/20', text: 'text-red-400', icon: '🔴' };
        if (reportCount >= 3) return { bg: 'bg-yellow-600/20', text: 'text-yellow-400', icon: '🟡' };
        return { bg: 'bg-blue-600/20', text: 'text-blue-400', icon: '🔵' };
    };

    return (
        <div className="min-h-screen" style={{ backgroundColor: '#1a0a0e' }}>
            {/* Header */}
            <header
                className="border-b px-6 py-4 flex items-center justify-between"
                style={{
                    backgroundColor: '#2e0510',
                    borderColor: 'rgba(255, 200, 87, 0.1)'
                }}
            >
                <div className="flex items-center gap-3">
                    <Shield className="w-8 h-8 text-[#e74e4a]" />
                    <div>
                        <h1 className="text-xl font-bold text-[#FFC857]">RadChat Admin Console</h1>
                        <p className="text-sm text-[#F5E3D7]/60">Central Administration</p>
                    </div>
                </div>

                <div className="flex items-center gap-4">
                    <span className="text-[#F5E3D7]/80">Welcome, {user?.username}</span>
                    <Button
                        onClick={handleSignOut}
                        variant="outline"
                        className="border-[#3d1a24] text-[#F5E3D7] hover:bg-[#3d1a24]"
                    >
                        <LogOut className="w-4 h-4 mr-2" />
                        Sign Out
                    </Button>
                </div>
            </header>

            <div className="flex">
                {/* Sidebar */}
                <aside
                    className="w-64 min-h-[calc(100vh-73px)] border-r p-4"
                    style={{
                        backgroundColor: '#260e10',
                        borderColor: 'rgba(255, 200, 87, 0.1)'
                    }}
                >
                    <nav className="space-y-2">
                        {[
                            { id: 'overview', label: 'Overview', icon: Activity },
                            { id: 'reports', label: 'Reports Queue', icon: Flag, badge: reports.filter(r => r.status !== 'resolved').length },
                            { id: 'feedback', label: 'Feedback', icon: HelpCircle, badge: feedback.filter(f => f.status === 'new').length },
                            { id: 'popups', label: 'All Popups', icon: MessageSquare },
                            { id: 'users', label: 'All Users', icon: Users },
                        ].map(item => (
                            <button
                                key={item.id}
                                onClick={() => setActiveTab(item.id)}
                                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-all ${activeTab === item.id
                                    ? 'bg-[#FFC857] text-[#2e0510]'
                                    : 'text-[#F5E3D7] hover:bg-[#3d1a24]'
                                    }`}
                            >
                                <item.icon className="w-5 h-5" />
                                {item.label}
                                {item.badge > 0 && (
                                    <span className="ml-auto bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                                        {item.badge}
                                    </span>
                                )}
                            </button>
                        ))}
                    </nav>
                </aside>

                {/* Main Content */}
                <main className="flex-1 p-6">
                    {activeTab === 'overview' && (
                        <div className="space-y-6">
                            <h2 className="text-2xl font-bold text-[#FFC857]">Dashboard Overview</h2>

                            {/* Stats Cards */}
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                <Card className="border-0" style={{ backgroundColor: '#260e10' }}>
                                    <CardContent className="p-6">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-[#F5E3D7]/60 text-sm">Total Users</p>
                                                <p className="text-3xl font-bold text-[#FFC857]">{stats.totalUsers}</p>
                                            </div>
                                            <Users className="w-10 h-10 text-[#e74e4a]" />
                                        </div>
                                    </CardContent>
                                </Card>

                                <Card className="border-0" style={{ backgroundColor: '#260e10' }}>
                                    <CardContent className="p-6">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-[#F5E3D7]/60 text-sm">Total Popups</p>
                                                <p className="text-3xl font-bold text-[#FFC857]">{stats.totalPopups}</p>
                                            </div>
                                            <MessageSquare className="w-10 h-10 text-[#e74e4a]" />
                                        </div>
                                    </CardContent>
                                </Card>

                                <Card className="border-0" style={{ backgroundColor: '#260e10' }}>
                                    <CardContent className="p-6">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-[#F5E3D7]/60 text-sm">Total Messages</p>
                                                <p className="text-3xl font-bold text-[#FFC857]">{stats.totalMessages}</p>
                                            </div>
                                            <TrendingUp className="w-10 h-10 text-[#e74e4a]" />
                                        </div>
                                    </CardContent>
                                </Card>

                                <Card className="border-0" style={{ backgroundColor: '#260e10' }}>
                                    <CardContent className="p-6">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-[#F5E3D7]/60 text-sm">Active Popups</p>
                                                <p className="text-3xl font-bold text-[#FFC857]">{stats.activePopups}</p>
                                            </div>
                                            <Radio className="w-10 h-10 text-[#e74e4a]" />
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>

                            {/* Recent Activity */}
                            <Card className="border-0" style={{ backgroundColor: '#260e10' }}>
                                <CardHeader>
                                    <CardTitle className="text-[#FFC857]">Recent Popups</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-3">
                                        {popups.slice(0, 5).map(popup => (
                                            <div
                                                key={popup.id}
                                                className="flex items-center justify-between p-3 rounded-lg"
                                                style={{ backgroundColor: '#1a0a0e' }}
                                            >
                                                <div>
                                                    <p className="font-medium text-[#F5E3D7]">{popup.name}</p>
                                                    <p className="text-sm text-[#F5E3D7]/60">Admin: {popup.admin_username}</p>
                                                </div>
                                                <span className="text-xs text-[#FFC857]">
                                                    {popup.member_count || 0} members
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    )}

                    {activeTab === 'reports' && (
                        <div className="space-y-6">
                            <div className="flex items-center justify-between">
                                <h2 className="text-2xl font-bold text-[#FFC857]">Reports Queue</h2>
                                <div className="flex gap-2">
                                    {['all', 'pending', 'under_review', 'resolved'].map(filter => (
                                        <button
                                            key={filter}
                                            onClick={() => setReportFilter(filter)}
                                            className={`px-3 py-1.5 rounded text-sm ${reportFilter === filter
                                                ? 'bg-[#FFC857] text-[#2e0510]'
                                                : 'bg-[#3d1a24] text-[#F5E3D7]'
                                                }`}
                                        >
                                            {filter.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {filteredReports.length === 0 ? (
                                <Card className="border-0" style={{ backgroundColor: '#260e10' }}>
                                    <CardContent className="p-8 text-center">
                                        <Flag className="w-12 h-12 text-[#F5E3D7]/30 mx-auto mb-3" />
                                        <p className="text-[#F5E3D7]/60">No reports found</p>
                                    </CardContent>
                                </Card>
                            ) : (
                                <div className="space-y-3">
                                    {filteredReports.map(report => {
                                        const priority = getPriorityColor(report.report_count);
                                        return (
                                            <Card key={report.report_id} className="border-0" style={{ backgroundColor: '#260e10' }}>
                                                <CardContent className="p-4">
                                                    <div className="flex items-start justify-between">
                                                        <div className="flex-1">
                                                            <div className="flex items-center gap-2 mb-2">
                                                                <span className="text-lg">{priority.icon}</span>
                                                                <span className={`px-2 py-0.5 rounded text-xs ${priority.bg} ${priority.text}`}>
                                                                    {report.report_count} report{report.report_count > 1 ? 's' : ''}
                                                                </span>
                                                                <span className={`px-2 py-0.5 rounded text-xs ${report.status === 'pending' ? 'bg-yellow-600/20 text-yellow-400' :
                                                                    report.status === 'under_review' ? 'bg-orange-600/20 text-orange-400' :
                                                                        'bg-green-600/20 text-green-400'
                                                                    }`}>
                                                                    {report.status.replace('_', ' ')}
                                                                </span>
                                                                <span className="px-2 py-0.5 rounded text-xs bg-purple-600/20 text-purple-400">
                                                                    {report.content_type}
                                                                </span>
                                                                <span className="px-2 py-0.5 rounded text-xs bg-gray-600/20 text-gray-400">
                                                                    {report.reason}
                                                                </span>
                                                            </div>

                                                            <div className="p-3 rounded-lg mb-3" style={{ backgroundColor: '#1a0a0e' }}>
                                                                <p className="text-[#F5E3D7] text-sm">
                                                                    {report.content_snapshot?.text || 'No content preview'}
                                                                </p>
                                                                {report.content_snapshot?.username && (
                                                                    <p className="text-[#F5E3D7]/50 text-xs mt-1">
                                                                        By: @{report.content_snapshot.username}
                                                                    </p>
                                                                )}
                                                            </div>

                                                            <p className="text-xs text-[#F5E3D7]/50">
                                                                ID: {report.report_id} • Reported: {new Date(report.created_at).toLocaleString()}
                                                            </p>
                                                        </div>

                                                        {report.status !== 'resolved' && (
                                                            <div className="flex gap-2 ml-4">
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline"
                                                                    onClick={() => setActionDialog({ open: true, report, action: 'restore' })}
                                                                    className="border-green-600 text-green-400 hover:bg-green-600/20"
                                                                    title="Restore (no violation)"
                                                                >
                                                                    <RotateCcw className="w-4 h-4" />
                                                                </Button>
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline"
                                                                    onClick={() => setActionDialog({ open: true, report, action: 'warn' })}
                                                                    className="border-yellow-600 text-yellow-400 hover:bg-yellow-600/20"
                                                                    title="Warn user"
                                                                >
                                                                    <AlertTriangle className="w-4 h-4" />
                                                                </Button>
                                                                <Button
                                                                    size="sm"
                                                                    variant="destructive"
                                                                    onClick={() => setActionDialog({ open: true, report, action: 'delete' })}
                                                                    className="bg-red-600 hover:bg-red-700"
                                                                    title="Delete content"
                                                                >
                                                                    <Trash2 className="w-4 h-4" />
                                                                </Button>
                                                                <Button
                                                                    size="sm"
                                                                    variant="destructive"
                                                                    onClick={() => setActionDialog({ open: true, report, action: 'ban' })}
                                                                    className="bg-purple-600 hover:bg-purple-700"
                                                                    title="Ban user"
                                                                >
                                                                    <Ban className="w-4 h-4" />
                                                                </Button>
                                                            </div>
                                                        )}

                                                        {report.status === 'resolved' && (
                                                            <div className="flex items-center gap-2 text-green-400">
                                                                <CheckCircle className="w-5 h-5" />
                                                                <span className="text-sm">{report.action_taken}</span>
                                                            </div>
                                                        )}
                                                    </div>
                                                </CardContent>
                                            </Card>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'popups' && (
                        <div className="space-y-6">
                            <div className="flex items-center justify-between">
                                <h2 className="text-2xl font-bold text-[#FFC857]">All Popups</h2>
                                <div className="relative w-64">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#F5E3D7]/50" />
                                    <Input
                                        placeholder="Search popups..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="pl-10 border-0"
                                        style={{ backgroundColor: '#260e10', color: '#F5E3D7' }}
                                    />
                                </div>
                            </div>

                            <Card className="border-0" style={{ backgroundColor: '#260e10' }}>
                                <CardContent className="p-0">
                                    <table className="w-full">
                                        <thead>
                                            <tr className="border-b" style={{ borderColor: 'rgba(255, 200, 87, 0.1)' }}>
                                                <th className="text-left p-4 text-[#FFC857] font-medium">Name</th>
                                                <th className="text-left p-4 text-[#FFC857] font-medium">Admin</th>
                                                <th className="text-left p-4 text-[#FFC857] font-medium">Members</th>
                                                <th className="text-left p-4 text-[#FFC857] font-medium">Type</th>
                                                <th className="text-left p-4 text-[#FFC857] font-medium">Created</th>
                                                <th className="text-right p-4 text-[#FFC857] font-medium">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredPopups.map(popup => (
                                                <tr
                                                    key={popup.id}
                                                    className="border-b hover:bg-[#1a0a0e]"
                                                    style={{ borderColor: 'rgba(255, 200, 87, 0.05)' }}
                                                >
                                                    <td className="p-4 text-[#F5E3D7]">{popup.name}</td>
                                                    <td className="p-4 text-[#F5E3D7]/80">{popup.admin_username}</td>
                                                    <td className="p-4 text-[#F5E3D7]/80">{popup.member_count || 0}</td>
                                                    <td className="p-4">
                                                        <span className={`px-2 py-1 rounded text-xs ${popup.is_personal
                                                            ? 'bg-purple-600/20 text-purple-400'
                                                            : 'bg-green-600/20 text-green-400'
                                                            }`}>
                                                            {popup.is_personal ? 'Personal' : 'Public'}
                                                        </span>
                                                    </td>
                                                    <td className="p-4 text-[#F5E3D7]/60 text-sm">
                                                        {new Date(popup.created_at).toLocaleDateString()}
                                                    </td>
                                                    <td className="p-4 text-right">
                                                        <Button
                                                            size="sm"
                                                            variant="destructive"
                                                            onClick={() => setDeleteDialog({ open: true, popup })}
                                                            className="bg-red-600 hover:bg-red-700"
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </CardContent>
                            </Card>
                        </div>
                    )}

                    {activeTab === 'users' && (
                        <div className="space-y-6">
                            <div className="flex items-center justify-between">
                                <h2 className="text-2xl font-bold text-[#FFC857]">All Users</h2>
                                <div className="relative w-64">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#F5E3D7]/50" />
                                    <Input
                                        placeholder="Search users..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="pl-10 border-0"
                                        style={{ backgroundColor: '#260e10', color: '#F5E3D7' }}
                                    />
                                </div>
                            </div>

                            <Card className="border-0" style={{ backgroundColor: '#260e10' }}>
                                <CardContent className="p-0">
                                    <table className="w-full">
                                        <thead>
                                            <tr className="border-b" style={{ borderColor: 'rgba(255, 200, 87, 0.1)' }}>
                                                <th className="text-left p-4 text-[#FFC857] font-medium">Username</th>
                                                <th className="text-left p-4 text-[#FFC857] font-medium">Email</th>
                                                <th className="text-left p-4 text-[#FFC857] font-medium">Role</th>
                                                <th className="text-left p-4 text-[#FFC857] font-medium">Joined</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredUsers.map(u => (
                                                <tr
                                                    key={u.id}
                                                    className="border-b hover:bg-[#1a0a0e]"
                                                    style={{ borderColor: 'rgba(255, 200, 87, 0.05)' }}
                                                >
                                                    <td className="p-4 text-[#F5E3D7]">{u.username}</td>
                                                    <td className="p-4 text-[#F5E3D7]/80">{u.email}</td>
                                                    <td className="p-4">
                                                        <span className={`px-2 py-1 rounded text-xs ${u.role === 'super_admin'
                                                            ? 'bg-yellow-600/20 text-yellow-400'
                                                            : 'bg-blue-600/20 text-blue-400'
                                                            }`}>
                                                            {u.role === 'super_admin' ? 'Super Admin' : 'User'}
                                                        </span>
                                                    </td>
                                                    <td className="p-4 text-[#F5E3D7]/60 text-sm">
                                                        {new Date(u.created_at).toLocaleDateString()}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </CardContent>
                            </Card>
                        </div>
                    )}

                    {/* Feedback Tab */}
                    {activeTab === 'feedback' && (
                        <div className="space-y-6">
                            <h2 className="text-2xl font-bold text-[#FFC857]">User Feedback</h2>

                            {feedback.length === 0 ? (
                                <Card style={{ backgroundColor: '#260e10', borderColor: 'rgba(255, 200, 87, 0.1)' }}>
                                    <CardContent className="p-8 text-center">
                                        <HelpCircle className="w-12 h-12 mx-auto mb-4 text-[#FFC857]/40" />
                                        <p className="text-[#F5E3D7]/60">No feedback received yet</p>
                                    </CardContent>
                                </Card>
                            ) : (
                                <div className="space-y-4">
                                    {feedback.map((fb) => (
                                        <Card
                                            key={fb.id}
                                            style={{
                                                backgroundColor: '#260e10',
                                                borderColor: fb.status === 'new' ? 'rgba(78, 205, 196, 0.3)' : 'rgba(255, 200, 87, 0.1)'
                                            }}
                                        >
                                            <CardContent className="p-4">
                                                <div className="flex items-start justify-between">
                                                    <div className="flex items-start gap-3">
                                                        <div
                                                            className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold"
                                                            style={{ background: 'linear-gradient(135deg, #FFC857 0%, #e7722eff 100%)', color: '#47030F' }}
                                                        >
                                                            {fb.username.slice(0, 2).toUpperCase()}
                                                        </div>
                                                        <div className="flex-1">
                                                            <div className="flex items-center gap-2 mb-1">
                                                                <span className="font-semibold text-[#F5E3D7]">{fb.username}</span>
                                                                <span
                                                                    className="px-2 py-0.5 rounded text-xs font-medium"
                                                                    style={{
                                                                        backgroundColor: fb.type === 'bug' ? 'rgba(239, 68, 68, 0.2)' :
                                                                            fb.type === 'feature' ? 'rgba(59, 130, 246, 0.2)' :
                                                                                fb.type === 'question' ? 'rgba(16, 185, 129, 0.2)' :
                                                                                    'rgba(107, 114, 128, 0.2)',
                                                                        color: fb.type === 'bug' ? '#EF4444' :
                                                                            fb.type === 'feature' ? '#3B82F6' :
                                                                                fb.type === 'question' ? '#10B981' :
                                                                                    '#6B7280'
                                                                    }}
                                                                >
                                                                    {fb.type === 'bug' ? '🐛 Bug' :
                                                                        fb.type === 'feature' ? '💡 Feature' :
                                                                            fb.type === 'question' ? '❓ Question' :
                                                                                '📝 Other'}
                                                                </span>
                                                            </div>
                                                            <p className="text-sm text-[#F5E3D7]/60 mb-2">{fb.email}</p>
                                                            <p className="text-[#F5E3D7] whitespace-pre-wrap">{fb.message}</p>
                                                            <p className="text-xs text-[#F5E3D7]/40 mt-2">
                                                                {new Date(fb.created_at).toLocaleString()}
                                                            </p>
                                                        </div>
                                                    </div>
                                                    <div className="flex flex-col items-end gap-2">
                                                        <span
                                                            className="px-2 py-1 rounded text-xs font-medium"
                                                            style={{
                                                                backgroundColor: fb.status === 'new' ? 'rgba(78, 205, 196, 0.2)' :
                                                                    fb.status === 'reviewed' ? 'rgba(255, 200, 87, 0.2)' :
                                                                        'rgba(107, 114, 128, 0.2)',
                                                                color: fb.status === 'new' ? '#4ECDC4' :
                                                                    fb.status === 'reviewed' ? '#FFC857' :
                                                                        '#6B7280'
                                                            }}
                                                        >
                                                            {fb.status.toUpperCase()}
                                                        </span>
                                                        <div className="flex gap-2">
                                                            {fb.status === 'new' && (
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline"
                                                                    onClick={() => handleFeedbackStatus(fb.id, 'reviewed')}
                                                                    className="text-[#FFC857] border-[#FFC857]/30 hover:bg-[#FFC857]/10"
                                                                >
                                                                    <Eye className="w-3 h-3 mr-1" />
                                                                    Reviewed
                                                                </Button>
                                                            )}
                                                            {fb.status !== 'resolved' && (
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline"
                                                                    onClick={() => handleFeedbackStatus(fb.id, 'resolved')}
                                                                    className="text-green-400 border-green-400/30 hover:bg-green-400/10"
                                                                >
                                                                    <CheckCircle className="w-3 h-3 mr-1" />
                                                                    Resolve
                                                                </Button>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </main>
            </div>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ open, popup: null })}>
                <DialogContent style={{ backgroundColor: '#260e10', borderColor: 'rgba(255, 200, 87, 0.2)' }}>
                    <DialogHeader>
                        <DialogTitle className="text-[#FFC857] flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-red-500" />
                            Delete Popup
                        </DialogTitle>
                        <DialogDescription className="text-[#F5E3D7]/70">
                            Are you sure you want to delete "{deleteDialog.popup?.name}"?
                            This will remove all messages and cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setDeleteDialog({ open: false, popup: null })}
                            className="border-[#3d1a24] text-[#F5E3D7] hover:bg-[#3d1a24]"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleDeletePopup}
                            className="bg-red-600 hover:bg-red-700"
                        >
                            Delete Popup
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Report Action Confirmation Dialog */}
            <Dialog open={actionDialog.open} onOpenChange={(open) => setActionDialog({ open, report: null, action: null })}>
                <DialogContent style={{ backgroundColor: '#260e10', borderColor: 'rgba(255, 200, 87, 0.2)' }}>
                    <DialogHeader>
                        <DialogTitle className="text-[#FFC857] flex items-center gap-2">
                            {actionDialog.action === 'restore' && <RotateCcw className="w-5 h-5 text-green-500" />}
                            {actionDialog.action === 'warn' && <AlertTriangle className="w-5 h-5 text-yellow-500" />}
                            {actionDialog.action === 'delete' && <Trash2 className="w-5 h-5 text-red-500" />}
                            {actionDialog.action === 'ban' && <Ban className="w-5 h-5 text-purple-500" />}
                            {actionDialog.action === 'restore' ? 'Restore Content' :
                                actionDialog.action === 'warn' ? 'Warn User' :
                                    actionDialog.action === 'delete' ? 'Delete Content' :
                                        actionDialog.action === 'ban' ? 'Ban User' : 'Take Action'}
                        </DialogTitle>
                        <DialogDescription className="text-[#F5E3D7]/70">
                            {actionDialog.action === 'restore' && 'This will restore the content and mark the report as resolved with no violation.'}
                            {actionDialog.action === 'warn' && 'This will send a warning to the user and count towards their violation record.'}
                            {actionDialog.action === 'delete' && 'This will permanently delete the reported content.'}
                            {actionDialog.action === 'ban' && 'This will permanently ban the user from RadChat.'}
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setActionDialog({ open: false, report: null, action: null })}
                            className="border-[#3d1a24] text-[#F5E3D7] hover:bg-[#3d1a24]"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={() => handleReportAction(actionDialog.action)}
                            className={
                                actionDialog.action === 'restore' ? 'bg-green-600 hover:bg-green-700' :
                                    actionDialog.action === 'warn' ? 'bg-yellow-600 hover:bg-yellow-700' :
                                        actionDialog.action === 'delete' ? 'bg-red-600 hover:bg-red-700' :
                                            'bg-purple-600 hover:bg-purple-700'
                            }
                        >
                            Confirm {actionDialog.action?.replace(/\b\w/g, l => l.toUpperCase())}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default AdminDashboard;
